import { useState, Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { MagnifyingGlass, Buildings, Gear, ListChecks, List, X, Phone, Lightning } from "@phosphor-icons/react";
import { MegaMenuDrawer } from "./MegaMenuDrawer";
import { MegaMenu } from "./MegaMenu";
import { PlansMenu } from "./PlansMenu";
import { PLAN_LINKS } from "../lib/plansConfig";
import { COURSE_MEGA_MENUS } from "../lib/megaMenuConfig";
import { AuthButton } from "./AuthButton";
import { HeaderCollegeSearch } from "./HeaderCollegeSearch";
import { LeadForm } from "./LeadForm";
import { useAuth } from "../lib/AuthProvider";

const NAV_ITEMS = [
  { path: "/", label: "HOME", icon: null, hideOnTablet: true },
  { path: "/ug-neet/predict", label: "PREDICTOR", icon: MagnifyingGlass },
  { path: "/colleges", label: "COLLEGES", icon: Buildings, hideOnTablet: true },
  { path: "/counselling", label: "COUNSELLING", icon: ListChecks },
  { path: "/admin", label: "ADMIN", icon: Gear, adminOnly: true },
];

const PHONE = "9986143554";

export default function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const { isAdmin } = useAuth();
  const items = NAV_ITEMS.filter((i) => !i.adminOnly || isAdmin);

  return (
    <nav className="w-full sticky top-0 z-50" data-testid="navbar">
      {/* ── MOBILE HEADER — single row ─────────────────────────────────── */}
      <div className="md:hidden bg-white border-b border-zinc-200" data-testid="mobile-header">
        <div className="px-3 flex items-center gap-2 h-14">
          <MegaMenuDrawer />
          <Link to="/" className="shrink-0" data-testid="navbar-logo-mobile" onClick={() => setMobileOpen(false)}>
            <img src="/logo.png" alt="Unicators" className="w-9 h-9 rounded-md object-cover" />
          </Link>
          <HeaderCollegeSearch className="flex-1 min-w-0" />
          <button
            onClick={() => setLeadOpen(true)}
            data-testid="navbar-enquire-btn-mobile"
            className="animate-blink flex items-center gap-1 shrink-0 h-8 px-3 rounded-full bg-red-600 text-white text-[11px] font-bold uppercase tracking-wide"
          >
            <Lightning size={12} weight="fill" />
            Enquire
          </button>
          <button
            className="w-9 h-9 flex items-center justify-center text-[#09090B] shrink-0"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </div>

      {/* ── ROW 1 — brand + guidance + priority callback (desktop) ─────── */}
      <div className="hidden md:block bg-white border-b border-zinc-100">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-14 gap-3">
          {/* Left: Logo + wordmark */}
          <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="navbar-logo" onClick={() => setMobileOpen(false)}>
            <img
              src="/logo.png"
              alt="Unicators"
              className="w-9 h-9 rounded-md object-cover shrink-0"
            />
            <span className="hidden sm:inline text-2xl leading-none tracking-tight text-[#09090B] whitespace-nowrap">
              <span
                className="italic font-black"
                style={{ fontFamily: "'Fraunces', 'Outfit', serif", letterSpacing: "-0.03em" }}
              >
                Uni
              </span>
              <span
                className="font-medium text-[#6043EF] uppercase text-[17px] align-baseline ml-0.5"
                style={{ fontFamily: "'Outfit', system-ui, sans-serif", letterSpacing: "0.14em" }}
              >
                cators
              </span>
            </span>
          </Link>

          {/* Center: admission guidance + phone (desktop only) */}
          <div className="hidden lg:flex items-center gap-4 flex-1 justify-center min-w-0" data-testid="navbar-guidance">
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#3F3F46]">
              <span className="text-[#09090B]">Admission guidance:</span>{" "}
              <span className="text-[#6043EF]">MBBS</span>
              <span className="text-zinc-300 mx-1">|</span>
              <span className="text-[#6043EF]">BDS</span>
              <span className="text-zinc-300 mx-1">|</span>
              <span className="text-[#6043EF]">Nursing</span>
            </span>
            <a
              href={`tel:+91${PHONE}`}
              data-testid="navbar-phone"
              className="flex items-center gap-1.5 rounded-full border-2 border-emerald-600 bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-[#09090B] shadow-[2px_2px_0px_0px_#059669] hover:shadow-[3px_3px_0px_0px_#059669] hover:-translate-y-0.5 transition-all"
            >
              <Phone size={14} weight="fill" className="text-emerald-600" />
              {PHONE}
            </a>
          </div>

          {/* Right: mega menu */}
          <div className="flex items-center shrink-0">
            {/* Desktop: white course mega-menu bar with orange links + orange border */}
            <div className="inline-flex relative items-center bg-white border border-[#EA580C] rounded-sm">
              {COURSE_MEGA_MENUS.map((m) => (
                <MegaMenu key={m.id} label={m.label} sections={m.sections} tone="orangeOutline" anchorToBar />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── ROW 2 — search + primary nav (desktop) ────────────────────── */}
      <div className="hidden md:block bg-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-14 gap-2">
          {/* Left: Enquire Now CTA (desktop) */}
          <button
            onClick={() => setLeadOpen(true)}
            data-testid="navbar-enquire-btn"
            className="hidden sm:flex animate-blink items-center gap-2 shrink-0 h-9 px-4 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wide transition-colors"
          >
            <Lightning size={14} weight="fill" />
            Enquire Now
          </button>

          {/* College search */}
          <HeaderCollegeSearch className="flex-1 min-w-0 max-w-xs mx-2 sm:mx-4" />

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-0">
            {items.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Fragment key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`px-2.5 h-14 items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] border-b-2 transition-colors duration-150 ease-out ${
                      item.hideOnTablet ? "hidden lg:flex" : "flex"
                    } ${
                      isActive
                        ? "border-[#6043EF] text-[#6043EF]"
                        : "border-transparent text-[#52525B] hover:text-[#09090B] hover:border-zinc-300"
                    }`}
                  >
                    {Icon && <Icon size={14} weight={isActive ? "bold" : "regular"} />}
                    {item.label}
                  </Link>
                  {item.path === "/counselling" && <PlansMenu />}
                </Fragment>
              );
            })}
            <div className="ml-2 pl-2 border-l border-zinc-200 h-14 flex items-center">
              <AuthButton />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t border-zinc-200 bg-white" data-testid="mobile-menu">
          {items.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] border-l-2 transition-colors ${
                  isActive
                    ? "border-[#6043EF] text-[#6043EF] bg-[#6043EF]/5"
                    : "border-transparent text-[#52525B] hover:bg-zinc-50"
                }`}
              >
                {Icon && <Icon size={14} weight={isActive ? "bold" : "regular"} />}
                {item.label}
              </Link>
            );
          })}
          <div className="border-t border-zinc-100 bg-[#F7F5FF]/40" data-testid="mobile-plans-section">
            <div className="px-5 pt-3 pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#6043EF]">Plans</div>
            {PLAN_LINKS.map((p) => (
              <Link
                key={p.id}
                to={p.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`mobile-plans-link-${p.id}`}
                className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-[#3F3F46] hover:bg-white transition-colors"
              >
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.accent }} />
                {p.label}
              </Link>
            ))}
          </div>
          <a
            href={`tel:+91${PHONE}`}
            data-testid="mobile-nav-phone"
            className="flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#09090B] border-t border-zinc-100"
          >
            <Phone size={14} weight="fill" className="text-emerald-600" />
            Call {PHONE}
          </a>
          <div className="px-5 py-3 border-t border-zinc-100">
            <AuthButton />
          </div>
        </div>
      )}

      {/* Priority callback lead modal */}
      <LeadForm open={leadOpen} onOpenChange={setLeadOpen} />
    </nav>
  );
}
